import { LogLevel } from "@hotelchamp/common";
import * as Sentry from "@sentry/react";
import { mn } from "date-fns/locale";

import { logLocation } from "./utils";

export type TLogger = typeof Sentry.logger;

// Very small helper to set nested metadata keys using dot-notation (Arr::set equivalent)
function setByPath(obj: Record<string, unknown> | undefined, path: string, value: unknown) {
    if (!obj) {
        return;
    }

    const parts = path.split(".");
    let cur: Record<string, unknown> = obj;
    for (let i = 0; i < parts.length; i++) {
        const key = parts[i]!;
        if (i === parts.length - 1) {
            cur[key] = value as never;
        } else {
            const next = cur[key];
            if (typeof next !== "object" || next === null) {
                cur[key] = {};
            }
            cur = cur[key] as Record<string, unknown>;
        }
    }
}

const mapLevelToFn = (level: LogLevel, logger: TLogger) => {
    switch (level) {
        case LogLevel.Debug:
            return logger.debug;
        case LogLevel.Info:
            return logger.info;
        case LogLevel.Warning:
            return logger.warn;
        case LogLevel.Error:
            return logger.error;
        default:
            return logger.info;
    }
};

export interface IActivityLogParams {
    scope: string;
    message: string;
    action: string | null;
    where: string | null;
    metadata: Record<string, unknown>;
}

export class ActivityLog {
    public static logger: TLogger = Sentry.logger;

    private static groups: string[] = [];

    private static currentLogsPerGroup: Array<ActivityLog | undefined> = [];

    private static currentActivityLog: ActivityLog | null = null; // for parity; not strictly required

    private static logQueue: Array<{ level: LogLevel; message: string; meta?: Record<string, unknown> }> = [];

    private static hasBeenLogged = false;

    public scope: string;
    public message: string;
    public action: string | null;
    public where: string | null;
    public metadata: Record<string, unknown> = {};

    private previousActivityLog: ActivityLog | null = null;
    private hasBeenQueued = false;

    public constructor({ scope, message, action, where, metadata }: IActivityLogParams) {
        const idx = ActivityLog.getCurrentGroupIndex();

        this.previousActivityLog = ActivityLog.currentLogsPerGroup[idx] ?? null;

        ActivityLog.currentLogsPerGroup[idx] = this;
        ActivityLog.currentActivityLog = this;

        this.scope = scope;
        this.message = message;
        this.action = action;
        this.where = where || logLocation(3);
        this.metadata = metadata;
    }

    public static create(...args: ConstructorParameters<typeof ActivityLog>) {
        return new ActivityLog(...args);
    }

    public static getCurrentActivityLog(): ActivityLog | null | undefined {
        return ActivityLog.currentLogsPerGroup[ActivityLog.getCurrentGroupIndex()];
    }

    public static clearClassState() {
        ActivityLog.currentLogsPerGroup = [];
        ActivityLog.logQueue = [];
        ActivityLog.currentActivityLog = null;
        ActivityLog.groups = [];
    }

    protected static log(level: LogLevel, message: string, meta?: Record<string, unknown>) {
        const fn = mapLevelToFn(level, this.logger);

        console.log("Logger", level, message, meta);

        fn.call(ActivityLog.logger, message, meta);
    }

    private static flushLogQueue() {
        if (ActivityLog.logQueue.length === 0) {
            return;
        }

        for (const item of ActivityLog.logQueue) {
            ActivityLog.log(item.level, item.message, item.meta);
        }

        ActivityLog.logQueue = [];
    }

    private static getCurrentGroupIndex(): number {
        return ActivityLog.groups.length > 0 ? ActivityLog.groups.length - 1 : 0;
    }

    public getLogParams(): [string, Record<string, unknown>] {
        return [
            String(this),
            {
                ...(this.metadata ?? {}),
                scope: this.scope,
                action: this.action ?? undefined,
                where: this.where ?? undefined,
            },
        ];
    }

    public toArray(): {
        message: string;
        scope: string;
        action: string | null;
        where: string | null;
        metadata: Record<string, unknown>;
    } {
        return {
            message: String(this),
            scope: this.scope,
            action: this.action,
            where: this.where,
            metadata: this.metadata ?? {},
        };
    }

    public takeFromPreviousLog(keys: Array<keyof ReturnType<ActivityLog["toArray"]>>, doOverride = false): ActivityLog {
        if (this.previousActivityLog) {
            const prev = this.previousActivityLog.toArray();
            const cur = this.toArray();

            for (const key of keys) {
                const prevHas = (prev as Record<string, unknown>)[key as string] !== undefined;
                const curIsNull = (cur as Record<string, unknown>)[key as string] === null;
                if (prevHas && (doOverride || curIsNull)) {
                    (this as any)[key] = (prev as any)[key];
                }
            }
        }
        return this;
    }

    public takeMetadataFromPreviousLog(keys: string[], doOverride = false): ActivityLog {
        if (this.previousActivityLog) {
            const [, prevMeta] = this.previousActivityLog.getLogParams();
            const [, curMeta] = this.getLogParams();

            for (const key of keys) {
                const prevHas = Object.prototype.hasOwnProperty.call(prevMeta, key);
                const curHas = Object.prototype.hasOwnProperty.call(curMeta, key);
                if (prevHas && (doOverride || !curHas)) {
                    setByPath(this.metadata, key, (prevMeta as Record<string, unknown>)[key]);
                }
            }
        }
        return this;
    }

    public getPreviousActivityLog(): ActivityLog | null {
        return this.previousActivityLog;
    }

    public getMetadata(): Record<string, unknown> | undefined {
        return this.metadata;
    }

    public toString(): string {
        const nbsp = "\u00A0";
        const prefix = ActivityLog.groups.length > 0 ? nbsp.repeat(4).repeat(ActivityLog.groups.length - 1) : "";
        const actionStr = this.action ? `[${this.action}]` : "";
        const whereStr = this.where ? `\n in ${this.where}` : "";

        return `${prefix}[${this.scope}]${actionStr} - ${this.message} - ${whereStr}`;
    }

    public info(): ActivityLog {
        this.log(LogLevel.Info, this.getLogParams());

        return this;
    }

    public debug(): ActivityLog {
        this.log(LogLevel.Debug, this.getLogParams());

        return this;
    }

    public warning(): ActivityLog {
        this.log(LogLevel.Warning, this.getLogParams());

        return this;
    }

    public error(): ActivityLog {
        this.log(LogLevel.Error, this.getLogParams());

        return this;
    }

    public queue(level: LogLevel): ActivityLog {
        const [message, meta] = this.getLogParams();

        ActivityLog.logQueue.push({ level, message, meta });

        this.hasBeenQueued = true;

        return this;
    }

    public startGroup(groupName: string): ActivityLog {
        if (ActivityLog.groups.includes(groupName)) {
            const msg = `Log group ${groupName} is already started.`;

            ActivityLog.log(LogLevel.Error, msg);

            return this;
        }

        ActivityLog.groups.push(groupName);

        const nbsp = "\u00A0";
        const prefix = ActivityLog.groups.length > 0 ? nbsp.repeat(4).repeat(ActivityLog.groups.length - 1) : "";
        const [, meta] = this.getLogParams();

        ActivityLog.logQueue.push({
            level: LogLevel.Warning,
            message: `${prefix}****************** START: ${groupName} ***************`,
            meta,
        });

        ActivityLog.logQueue.push({ level: LogLevel.Warning, message: `${prefix}*`, meta });

        return this;
    }

    public endGroup(): ActivityLog {
        const total = ActivityLog.groups.length;
        const last = ActivityLog.groups.pop();

        if (!last) {
            const msg = "All log groups are already ended.";

            ActivityLog.log(LogLevel.Error, msg);

            return this;
        }

        const nbsp = "\u00A0";
        const prefix = total > 0 ? nbsp.repeat(4).repeat(total - 1) : "";
        const [, meta] = this.getLogParams();

        ActivityLog.logQueue.push({ level: LogLevel.Warning, message: `${prefix}*`, meta });
        ActivityLog.logQueue.push({ level: LogLevel.Warning, message: `${prefix}****************** END: ${last} ***************`, meta });

        if (ActivityLog.hasBeenLogged) {
            ActivityLog.flushLogQueue();
        }

        return this;
    }

    private log(level: LogLevel, [message, meta]: [string, Record<string, unknown>], forced = false): void {
        if (ActivityLog.hasBeenLogged) {
            // throw new Error("This log was already logged. It cannot be logged again.");

            return;
        }

        if (this.hasBeenQueued && !forced) {
            // throw new Error("This log was already queued for logging on warning or error. It will be logged automatically");

            return;
        }

        ActivityLog.flushLogQueue();

        ActivityLog.log(level, message, meta);

        ActivityLog.hasBeenLogged = true;
    }
}
